import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaDownload, FaPhone, FaEnvelope, FaCheck, FaFileDownload, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { useApiData } from '../hooks/useApiData';
import { fetchPricing, submitContactForm } from '../repository/api';
import LoadingSpinner from '../components/LoadingSpinner';
import SEOHead from '../components/SEOHead';

// Fallback/Default pricing data (in case API fails)
const defaultPricingData = {
  page_heading: "Transparent & Flexible Pricing",
  page_paragraph: "Discover our cost-effective communication solutions with clear, straightforward pricing designed to deliver maximum value.",
  philosophy_heading: "Our Pricing Philosophy",
  philosophy_paragraph: "Our service offers transparent and customizable pricing options to accommodate different budgets and needs. There are no hidden fees, and clients can choose from various packages or customize their own. The focus is on delivering value and reliability to all customers, whether they are small businesses or larger enterprises.",
  packages: [
    {
      title: "Alphanumeric-Sender ID",
      amount: "KSh 11,600",
      paragraph: "VAT Inclusive, one-time per network",
      description: "A customizable sender name that appears instead of a phone number when messages are sent.",
      points: [
        "One-time setup fee",
        "No monthly charges",
        "Up to 11 characters",
        "Brand recognition"
      ],
      is_most_popular: false
    },
    {
      title: "Shared Short Code",
      amount: "KSh 11,600",
      paragraph: "VAT Inclusive, per network",
      description: "A short code shared with other businesses, with keyword-based routing.",
      points: [
        "KSh 11,600 setup fee",
        "KSh 11,600 monthly fee",
        "Quick implementation",
        "Cost-effective solution"
      ],
      is_most_popular: false
    },
    {
      title: "Dedicated Short Code",
      amount: "KSh 17,400",
      paragraph: "Exclusive of VAT, per mobile network",
      description: "Your exclusive short code that is unique to your business only.",
      points: [
        "KSh 17,400 setup fee",
        "KSh 17,400 monthly fee",
        "Unique to your business",
        "Enhanced brand visibility"
      ],
      is_most_popular: true
    }
  ],
  bulk_sms_pricing: [
    {
      quantity_range: "1-50,000",
      cost_per_unit: "0.70",
      savings_percentage: "-"
    },
    {
      quantity_range: "50,001-100,000",
      cost_per_unit: "0.60",
      savings_percentage: "14% savings"
    },
    {
      quantity_range: "100,001-500,000",
      cost_per_unit: "0.40",
      savings_percentage: "43% savings"
    },
    {
      quantity_range: "500,001-1,000,000",
      cost_per_unit: "0.30",
      savings_percentage: "57% savings"
    },
    {
      quantity_range: "Over 1,000,001",
      cost_per_unit: "0.20",
      savings_percentage: "71% savings"
    }
  ],
  why_choose: {
    heading: "Why Choose Our Bulk SMS Services?",
    reasons: [
      "99.9% delivery rate",
      "Real-time delivery reports",
      "24/7 technical support",
      "API integration available",
      "No hidden costs",
      "Free account setup"
    ]
  },
  requirements: {
    heading: "Requirements for Onboarding",
    paragraph: "To get started with our services, please prepare the following documents and information:",
    list: [
      {
        title: "Company Registration Certificate",
        explanation: "A valid business registration document from your local authority."
      },
      {
        title: "Letter of Authorization",
        explanation: "For alphanumeric/Sender ID. The proposed sender/Alphanumeric ID shouldn't be more than 11 Characters on your company letter head."
      },
      {
        title: "Proof of Payment",
        explanation: "For Alphanumeric and short code set up and quantity of Bulk SMS required."
      },
      {
        title: "SMS Portal Setup",
        explanation: "Upon satisfactorily complying with the above prerequisites Codewave shall provide you with a Bulk SMS Portal and preloaded with the required Bulk SMS credits."
      },
      {
        title: "API Documentation",
        explanation: "For API connectivity: The API documentation will be provided to allow communication to our endPoint."
      }
    ]
  }
};

const PricingPage = () => {
  // Fetch pricing data from API
  const { data: apiData, loading, error } = useApiData(fetchPricing);

  // Use API data if available, otherwise fall back to default data
  const pricingData = apiData || defaultPricingData;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      await submitContactForm(formData);
      setFormStatus({
        type: 'success',
        message: "Thank you! We'll get back to you soon."
      });
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
      });
    } catch (err) {
      setFormStatus({
        type: 'error',
        message: 'Failed to submit form. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show full-page loading spinner while fetching from API
  if (loading) {
    return <LoadingSpinner text="Loading pricing..." />;
  }

  // If API fails, we silently fall back to defaultPricingData
  // No error is shown to the user for better UX
  if (error && process.env.NODE_ENV === 'development') {
    console.warn('Pricing API failed, using fallback data:', error);
  }

  return (
    <PageWrapper>
      <SEOHead
        title="Bulk SMS Pricing Kenya | Affordable USSD & SMS Gateway Rates - Codewave Technologies Limited"
        description="Transparent and affordable bulk SMS pricing in Kenya. Get the best rates for bulk SMS services, USSD setup, short code services, and enterprise messaging solutions. Volume discounts available."
        keywords="bulk SMS pricing Kenya, affordable bulk SMS services in Kenya, SMS gateway pricing, USSD service pricing, short code setup cost Kenya, bulk SMS rates Nairobi, enterprise messaging pricing, business SMS marketing cost, telecom API pricing Kenya, mobile payment solutions pricing"
        url="https://www.codewave.co.ke/pricing"
        canonical="https://www.codewave.co.ke/pricing"
      />
      {/* Hero Section */}
      <HeroSection>
        <Container>
          <HeroContent>
            <BreadcrumbNav>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
              <BreadcrumbSeparator>/</BreadcrumbSeparator>
              <BreadcrumbCurrent>Pricing</BreadcrumbCurrent>
            </BreadcrumbNav>

            <HeroTitle>{pricingData.page_heading}</HeroTitle>

            <HeroDescription>
              {pricingData.page_paragraph}
            </HeroDescription>
          </HeroContent>
        </Container>
      </HeroSection>

      {/* Pricing Overview */}
      <Section>
        <Container>
          <SectionHeader>
            <SectionTitle>{pricingData.philosophy_heading}</SectionTitle>
            <SectionDescription>
              {pricingData.philosophy_paragraph}
            </SectionDescription>
          </SectionHeader>

          {/* Investment Options */}
          <SectionSubtitle>Investment Options</SectionSubtitle>

          <InvestmentGrid>
            {pricingData.packages && pricingData.packages.map((pkg, index) => (
              <InvestmentCard key={index} featured={pkg.is_most_popular}>
                {pkg.is_most_popular && <FeatureBadge>Most Popular</FeatureBadge>}
                <InvestmentTitle>{pkg.title}</InvestmentTitle>
                <InvestmentPrice>{pkg.amount}</InvestmentPrice>
                <PriceSubtext>{pkg.paragraph}</PriceSubtext>
                <InvestmentDescription>
                  {pkg.description}
                </InvestmentDescription>
                <FeaturesList>
                  {pkg.points && pkg.points.map((point, idx) => (
                    <FeatureItem key={idx}>
                      <FaCheckCircle /> {point}
                    </FeatureItem>
                  ))}
                </FeaturesList>
                <InvestmentButton featured={pkg.is_most_popular}>Get Started</InvestmentButton>
              </InvestmentCard>
            ))}
          </InvestmentGrid>
        </Container>
      </Section>

      {/* Bulk SMS Pricing Table */}
      <Section bgColor="#f8f9fb">
        <Container>
          <SectionHeader>
            <SectionTitle>Bulk SMS Pricing</SectionTitle>
            <SectionDescription>
              Our volume-based pricing ensures you get better rates as your messaging needs grow.
              All prices include VAT.
            </SectionDescription>
          </SectionHeader>

          <PricingCard>
            <TableWrapper>
              <PricingTable>
                <thead>
                  <tr>
                    <TableHeader>#</TableHeader>
                    <TableHeader>Quantity of Bulk SMS</TableHeader>
                    <TableHeader>Cost per unit (KSh)</TableHeader>
                    <TableHeader>Savings</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {pricingData.bulk_sms_pricing && pricingData.bulk_sms_pricing.map((pricing, index) => (
                    <TableRow key={index} highlighted={index === 2}>
                      <TableCell>{index + 1}.</TableCell>
                      <TableCell>{pricing.quantity_range}</TableCell>
                      <TableCell>{pricing.cost_per_unit}</TableCell>
                      <TableCell>{pricing.savings_percentage}</TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </PricingTable>
            </TableWrapper>
            <PricingNote>* All prices are VAT inclusive. Savings calculated against base rate.</PricingNote>
          </PricingCard>

          <ComparisonSection>
            <ComparisonTitle>{pricingData.why_choose?.heading}</ComparisonTitle>
            <ComparisonGrid>
              {pricingData.why_choose?.reasons && pricingData.why_choose.reasons.map((reason, index) => (
                <ComparisonItem key={index}>
                  <ComparisonIcon>
                    <FaCheck />
                  </ComparisonIcon>
                  <ComparisonText>{reason}</ComparisonText>
                </ComparisonItem>
              ))}
            </ComparisonGrid>
          </ComparisonSection>
        </Container>
      </Section>

      {/* Requirements Section */}
      <RequirementsSection>
        <Container>
          <SectionHeader>
            <SectionTitle>{pricingData.requirements?.heading}</SectionTitle>
            <SectionDescription>
              {pricingData.requirements?.paragraph}
            </SectionDescription>
          </SectionHeader>

          <RequirementsList>
            {pricingData.requirements?.list && pricingData.requirements.list.map((requirement, index) => (
              <RequirementItem key={index}>
                <RequirementNumber>{index + 1}</RequirementNumber>
                <RequirementContent>
                  <RequirementTitle>{requirement.title}</RequirementTitle>
                  <RequirementText>
                    {requirement.explanation}
                  </RequirementText>
                </RequirementContent>
              </RequirementItem>
            ))}
          </RequirementsList>
        </Container>
      </RequirementsSection>

      {/* Downloads Section */}
      {/* <Section>
        <Container>
          <SectionHeader>
            <SectionTitle>Download Brochures</SectionTitle>
            <SectionDescription>
              Get detailed information about our pricing and services in our downloadable resources.
            </SectionDescription>
          </SectionHeader>
          
          <DownloadsGrid>
            <DownloadCard>
              <DownloadIcon>
                <FaFileDownload />
              </DownloadIcon>
              <DownloadTitle>Pricing Guide</DownloadTitle>
              <DownloadDescription>
                Complete breakdown of all pricing options and volume discounts
              </DownloadDescription>
              <DownloadButton href="/brochures/pricing-guide.pdf" download>
                <FaDownload /> Download PDF
              </DownloadButton>
            </DownloadCard>
            
            <DownloadCard>
              <DownloadIcon>
                <FaFileDownload />
              </DownloadIcon>
              <DownloadTitle>Service Catalog</DownloadTitle>
              <DownloadDescription>
                Full details of all our messaging and communication services
              </DownloadDescription>
              <DownloadButton href="/brochures/service-catalog.pdf" download>
                <FaDownload /> Download PDF
              </DownloadButton>
            </DownloadCard>
            
            <DownloadCard>
              <DownloadIcon>
                <FaFileDownload />
              </DownloadIcon>
              <DownloadTitle>API Documentation</DownloadTitle>
              <DownloadDescription>
                Technical documentation for developers to integrate our services
              </DownloadDescription>
              <DownloadButton href="/brochures/api-docs.pdf" download>
                <FaDownload /> Download PDF
              </DownloadButton>
            </DownloadCard>
          </DownloadsGrid>
        </Container>
      </Section> */}

      {/* Contact Section */}
      <ContactSection>
        <Container>
          <ContactWrapper>
            <ContactInfo>
              <ContactHeader>
                <ContactTitle>Our team is ready to help you</ContactTitle>
                <ContactSubtitle>
                  Have questions about our pricing? Need a custom quote? Our team is here to assist you.
                </ContactSubtitle>
              </ContactHeader>

              <ContactMethod>
                <ContactMethodIcon>
                  <FaPhone />
                </ContactMethodIcon>
                <div>
                  <ContactMethodLabel>Talk to Us</ContactMethodLabel>
                  <ContactMethodValue>+254 700 123 456</ContactMethodValue>
                </div>
              </ContactMethod>

              <ContactMethod>
                <ContactMethodIcon>
                  <FaEnvelope />
                </ContactMethodIcon>
                <div>
                  <ContactMethodLabel>Email Us</ContactMethodLabel>
                  <ContactMethodValue>info@shulenetwork.com</ContactMethodValue>
                </div>
              </ContactMethod>
            </ContactInfo>

            <ContactFormWrapper>
              <ContactFormTitle>Contact Us</ContactFormTitle>
              {formStatus.message && (
                <FormStatusMessage type={formStatus.type}>
                  {formStatus.message}
                </FormStatusMessage>
              )}
              <ContactForm ref={formRef} onSubmit={handleSubmit}>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Your Name*</FormLabel>
                    <FormInput
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>Email Address*</FormLabel>
                    <FormInput
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your email address"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>Phone Number*</FormLabel>
                    <FormInput
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your phone number"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>Company</FormLabel>
                    <FormInput
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Enter your company name"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>Message*</FormLabel>
                    <FormTextarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows="4"
                      placeholder="Tell us about your needs or questions"
                    />
                  </FormGroup>
                </FormRow>

                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit'} {!isSubmitting && <FaArrowRight />}
                </SubmitButton>
              </ContactForm>
            </ContactFormWrapper>
          </ContactWrapper>
        </Container>
      </ContactSection>

      {/* CTA Section */}
      <CTASection>
        <Container>
          <CTAContent>
            <CTATitle>Ready to get started?</CTATitle>
            <CTADescription>
              Take advantage of our transparent pricing and flexible options for your business communication needs.
            </CTADescription>
            <CTAButtons>
              <PrimaryCTAButton href="/contact">
                Get Started Today
              </PrimaryCTAButton>
              <SecondaryCTAButton href="/services">
                Explore Services
              </SecondaryCTAButton>
            </CTAButtons>
          </CTAContent>
        </Container>
      </CTASection>
    </PageWrapper>
  );
};

export default PricingPage;

// === Animations ===
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

// === Styled Components ===
const PageWrapper = styled.div`
  background-color: white;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

// Hero Section
const HeroSection = styled.section`
  background: linear-gradient(135deg, #f8f9fb 0%, #f1f4f9 100%);
  padding: 100px 0 80px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 80px 0 60px;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const BreadcrumbNav = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  font-size: 14px;
`;

const BreadcrumbLink = styled.a`
  color: #666;
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover {
    color: #2090B0;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 10px;
  color: #ccc;
`;

const BreadcrumbCurrent = styled.span`
  color: #2090B0;
  font-weight: 500;
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #105090;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Generic Sections
const Section = styled.section`
  padding: 80px 0;
  background-color: ${props => props.bgColor || "white"};
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 50px;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #105090;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SectionDescription = styled.p`
  font-size: 18px;
  color: #666;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SectionSubtitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #105090;
  margin: 0 0 40px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 30px;
  }
`;

// Investment Cards
const InvestmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InvestmentCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  border: ${props => props.featured ? '2px solid #2090B0' : '1px solid #eee'};
  transform: ${props => props.featured ? 'scale(1.03)' : 'scale(1)'};
  z-index: ${props => props.featured ? '2' : '1'};
  animation: ${fadeIn} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 992px) {
    transform: scale(1);
    
    &:hover {
      transform: translateY(-10px);
    }
  }
`;

const FeatureBadge = styled.div`
  position: absolute;
  top: -15px;
  right: 20px;
  background: #2090B0;
  color: white;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 15px;
  border-radius: 20px;
  box-shadow: 0 5px 15px rgba(32, 144, 176, 0.3);
`;

const InvestmentTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: #105090;
  margin: 0 0 20px;
`;

const InvestmentPrice = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #105090;
  margin-bottom: 5px;
`;

const PriceSubtext = styled.div`
  font-size: 14px;
  color: #777;
  margin-bottom: 20px;
`;

const InvestmentDescription = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
  min-height: 48px;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 25px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  font-size: 15px;
  color: #555;
  margin-bottom: 12px;
  
  svg {
    color: ${props => props.checkColor || '#2090B0'};
    margin-right: 10px;
    font-size: 16px;
  }
`;

const InvestmentButton = styled.button`
  width: 100%;
  background: ${props => props.featured ? '#2090B0' : 'white'};
  color: ${props => props.featured ? 'white' : '#105090'};
  border: ${props => props.featured ? 'none' : '2px solid #105090'};
  font-size: 16px;
  font-weight: 600;
  padding: 12px 0;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.featured ? '#b02e6e' : '#105090'};
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

// Pricing Table
const PricingCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const PricingTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  background: #105090;
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 15px 20px;
  text-align: left;
  
  &:first-child {
    border-radius: 12px 0 0 0;
  }
  
  &:last-child {
    border-radius: 0 12px 0 0;
  }
`;

const TableRow = styled.tr`
  background: ${props => props.highlighted ? '#f7f5ff' : 'white'};
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const TableCell = styled.td`
  padding: 15px 20px;
  font-size: 15px;
  color: #444;
  border-bottom: 1px solid #eee;
  
  &:nth-child(3) {
    font-weight: 600;
    color: #105090;
  }
  
  &:nth-child(4) {
    font-weight: 500;
    color: #2090B0;
  }
`;

const PricingNote = styled.p`
  font-size: 14px;
  color: #777;
  padding: 15px 20px;
  margin: 0;
  font-style: italic;
`;

// Comparison Section
const ComparisonSection = styled.div`
  margin-top: 50px;
`;

const ComparisonTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: #105090;
  margin-bottom: 30px;
  text-align: center;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ComparisonItem = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: ${props => props.delay || 0}s;
  animation-fill-mode: both;
`;

const ComparisonIcon = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  background: rgba(32, 144, 176, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  
  svg {
    color: #2090B0;
    font-size: 14px;
  }
`;

const ComparisonText = styled.p`
  font-size: 15px;
  color: #444;
  margin: 0;
  font-weight: 500;
`;

// Requirements Section
const RequirementsSection = styled.section`
  padding: 80px 0;
  background: white;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const RequirementsList = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const RequirementItem = styled.div`
  display: flex;
  margin-bottom: 30px;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: ${props => props.delay || 0}s;
  animation-fill-mode: both;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const RequirementNumber = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  border-radius: 50%;
  background: #105090;
  color: white;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  margin-top: 5px;
`;

const RequirementContent = styled.div`
  flex: 1;
`;

const RequirementTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #105090;
  margin: 0 0 8px;
`;

const RequirementText = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #666;
  margin: 0;
`;

// Download Brochures
const DownloadsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const DownloadCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const DownloadIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #f8f9fb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  transition: all 0.3s ease;
  
  svg {
    font-size: 28px;
    color: #105090;
  }
  
  ${DownloadCard}:hover & {
    background: #105090;
    
    svg {
      color: white;
    }
  }
`;

const DownloadTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #105090;
  margin: 0 0 15px;
`;

const DownloadDescription = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 25px;
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: white;
  color: #105090;
  border: 2px solid #105090;
  font-size: 15px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 30px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: #105090;
    color: white;
    transform: translateY(-3px);
  }
  
  svg {
    font-size: 14px;
  }
`;

// Contact Section
const ContactSection = styled.section`
  padding: 80px 0;
  background: #f8f9fb;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const ContactWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactInfo = styled.div`
  animation: ${fadeIn} 0.6s ease-out;
`;

const ContactHeader = styled.div`
  margin-bottom: 40px;
`;

const ContactTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  color: #105090;
  margin: 0 0 20px;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const ContactSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 25px;
`;

const ContactMethodIcon = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  
  svg {
    font-size: 20px;
    color: #2090B0;
  }
`;

const ContactMethodLabel = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 5px;
`;

const ContactMethodValue = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #105090;
  margin: 0;
`;

const ContactFormWrapper = styled.div`
  background: white;
  border-radius: 15px;
  padding: 40px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.05);
  animation: ${slideInRight} 0.6s ease-out;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const ContactFormTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #105090;
  margin: 0 0 30px;
  text-align: center;
`;

const ContactForm = styled.form``;

const FormRow = styled.div`
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  width: 100%;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #444;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  color: #333;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #2090B0;
    box-shadow: 0 0 0 3px rgba(32, 144, 176, 0.1);
  }
  
  &::placeholder {
    color: #aaa;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  color: #333;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #2090B0;
    box-shadow: 0 0 0 3px rgba(32, 144, 176, 0.1);
  }
  
  &::placeholder {
    color: #aaa;
  }
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #2090B0;
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 12px 30px;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #b02e6e;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(32, 144, 176, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 14px;
    transition: transform 0.3s ease;
  }
  
  &:hover:not(:disabled) svg {
    transform: translateX(4px);
  }
`;

// CTA Section
const CTASection = styled.section`
  padding: 80px 0;
  background: linear-gradient(90deg, #105090, #2090B0);
  color: white;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const CTAContent = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 20px;
  
  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const CTADescription = styled.p`
  font-size: 18px;
  opacity: 0.9;
  margin: 0 0 30px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const PrimaryCTAButton = styled.a`
  display: inline-block;
  background: white;
  color: #105090;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 30px;
  border-radius: 30px;
  text-decoration: none;
  transition: all 0.3s ease;
  animation: ${pulse} 2s ease-in-out infinite;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    animation: none;
  }
  
  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 15px;
  }
`;

const SecondaryCTAButton = styled.a`
  display: inline-block;
  background: transparent;
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 12px 30px;
  border: 2px solid white;
  border-radius: 30px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    padding: 10px 25px;
    font-size: 15px;
  }
`;

// Form Status Message
const FormStatusMessage = styled.div`
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
  background: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : '#f5c6cb'};
`;
